import { QueryClient, useQuery } from '@tanstack/react-query'

import {
  VestingBalance,
  VestingBalancesResponse,
} from '~/components/smart-accounts/vesting/types'
import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_VESTING } from '~/constants/refetch-intervals'
import { useActiveOrgId } from '~/hooks/org/useActiveOrgId'
import { useServerRequests } from '~/hooks/useRequests'

export const useVestingBalances = (smartAccountId?: string, orgId?: string) => {
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()
  const effectiveOrgId = orgId ?? activeOrgId

  return useQuery<VestingBalance[]>({
    queryKey: [
      QUERY_KEYS.VESTING_ORG_ACCOUNT_BALANCES,
      effectiveOrgId,
      smartAccountId,
    ],
    enabled: !!smartAccountId,
    refetchInterval: REFETCH_VESTING,
    queryFn: async () => {
      if (!smartAccountId) {
        throw new Error('Smart account ID is required')
      }

      const response = await serverGetRequest<VestingBalancesResponse[]>({
        path: `/api/vesting/balances/${effectiveOrgId}`,
        params: {
          accountId: smartAccountId,
        },
      })

      if (!response) {
        throw new Error('Failed to fetch vesting balances')
      }

      return response.data.map((balance) => {
        return {
          ...balance,
          tokenId: BigInt(balance.tokenId),
          claimableAmount: BigInt(balance.claimableAmount),
          claimFee: balance.claimFee
            ? {
                amount: BigInt(balance.claimFee.amount),
                tokenAddress: balance.claimFee.tokenAddress,
              }
            : undefined,
          cliffTime: balance.cliffTime,
        }
      })
    },
  })
}

export const invalidateVestingBalancesQuery = (
  queryClient: QueryClient,
  activeOrgId?: string,
  smartAccountId?: string,
) => {
  if (!smartAccountId) {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.VESTING_ORG_ACCOUNT_BALANCES, activeOrgId],
      exact: false,
    })
    return
  }

  queryClient.invalidateQueries({
    queryKey: [
      QUERY_KEYS.VESTING_ORG_ACCOUNT_BALANCES,
      activeOrgId,
      smartAccountId,
    ],
  })
}
